import { Flex } from "@radix-ui/themes";
import { t } from "i18next";
import React, { Dispatch, SetStateAction } from "react";
import { Button } from "../../../ui/Button";
import { Popover, PopoverTrigger, PopoverContent } from "../../../ui/Popover";
import { ColumnFilter, ColumnFiltersState, SortingState } from "@tanstack/react-table";
import TableSortingCategory from "../../../components/Filters/TableSortingCategory";
import { Tab, useTabsStore } from "../../../stores/TabsStore";
import TableFilterCategory from "../../../components/Filters/TableFilterCategory";
import { Switch, SwitchSM } from "../../../ui/Switch/Switch";
import { TbX } from "react-icons/tb";
import { Input } from "../../../ui/Input/Input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../../ui/Dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../ui/Accordion";
import { ScrollArea } from "../../../ui/ScrollArea/ScrollArea";
import { Label } from "../../../ui/Label/Label";
import { SelectOption } from "../../../types/types";
import { Separator } from "../../../ui/Separator";

export type PersonsCategories = {
  unique_weekdays: Array<SelectOption>;
  unique_shifts: Array<SelectOption>;
  unique_persons: Array<SelectOption>;
  unique_days: Array<SelectOption>;
  unique_equipment_models: Array<SelectOption>;
  unique_equipments: Array<SelectOption>;
  unique_equipment_types: Array<SelectOption>;
  unique_card_types: Array<SelectOption>;
  unique_posts: Array<SelectOption>;
};

type Props = {
  filtersState: [ColumnFiltersState, Dispatch<SetStateAction<ColumnFiltersState>>];
  tab: Tab;
  categories: PersonsCategories;
  excludeWeekendState: [boolean, Function];
};

const PersonsFilters = ({ filtersState, tab, categories, excludeWeekendState }: Props) => {
  const [excludeWeekend, setExcludeWeekend] = excludeWeekendState;
  const [filters, setFilters] = filtersState; // Общий фильтр
  const addFilters = useTabsStore((store) => store.addFilters);

  const filtersColumns = [
    { categoryName: "days", columnId: "at", category: categories.unique_days },
    { categoryName: "weekdays", columnId: "weekday_short", category: categories.unique_weekdays },
    { categoryName: "shifts", columnId: "shift", category: categories.unique_shifts },
    { categoryName: "persons", columnId: "person", category: categories.unique_persons },
    { categoryName: "card_types", columnId: "card_type", category: categories.unique_card_types },
    { categoryName: "equipment_models", columnId: "equipment_model", category: categories.unique_equipment_models},
    { categoryName: "equipment_names", columnId: "equipment_name", category: categories.unique_equipments},
    { categoryName: "equipment_types", columnId: "equipment_type", category: categories.unique_equipment_types },
    { categoryName: "posts", columnId: "post", category: categories.unique_posts },
  ];

  const changeFilter = (columnId: string, newState: unknown, isEmpty: boolean) => {
    const editedFilter = filters.find((filter) => filter.id == columnId);
    let newFilters;
    if (editedFilter) {
      if (isEmpty) newFilters = [...filters.filter((filter) => filter.id != columnId)];
      else newFilters = [...filters.filter((filter) => filter.id != columnId), { id: columnId, value: newState }];
    } else {
      newFilters = [...filters, { id: columnId, value: newState }];
    }
    setFilters(newFilters);
    addFilters(tab.id, { ...tab.options?.filters, filters: newFilters });
  };

  const cleanFilter = () => {
    setFilters([]);
    setExcludeWeekend(false);
    addFilters(tab.id, { ...tab.options?.filters, filters: [], excludeWeekend: false });
  };

  const state: [ColumnFiltersState, Function] = [filters, changeFilter];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex flex-none items-center">
          <Button variant="outline" size="xs" className="px-0 items-center">
            <span className="px-2">{t("filters")}</span>
            {/* {filters.length > 0 || excludeWeekend == true ? <span className="border-l w-6 bg-muted h-full p-0.5 flex items-center justify-center hover:bg-destructive hover:text-white rounded-r-md"><TbX className="cursor-pointer" onClick={() => cleanFilter()} /></span> : <></>} */}
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent style={{ width: 320 }} align={"start"} className="flex flex-col h-full p-0 text-base">
        <div className="flex items-center justify-between px-3 h-10">
          <div className="leading-6 font-semibold">{t("filters")}</div>
          <Button variant="outline" size="xs" type="button" onClick={() => cleanFilter()}>
            {t("clear")}
          </Button>
        </div>
        <Separator />
        <div className="flex flex-col h-full py-2">
          <Accordion type="single" collapsible className="w-full text-sm">
            <AccordionItem value="parameters">
              <AccordionTrigger className="h-8 px-3 space-x-2">{t("parameters")}</AccordionTrigger>
              <AccordionContent className="bg-muted py-2 space-y-2">
                <div className="flex items-center justify-between w-full gap-2 px-3">
                  <Label className="w-full">{t('worked_more_than')} {t('in_seconds')}</Label>
                  <input
                    type="number"
                    className="flex h-6 w-16 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 focus:border-primary focus:border-1 focus:ring-primary focus:ring-1 focus:ring-inset"
                    value={
                      filters.find((filter) => filter.id == "worked")
                        ? (filters.find((filter) => filter.id == "worked")?.value as number)
                        : 0
                    }
                    onChange={(e) => {
                      changeFilter("worked", Number(e.target.value), false);
                    }}
                  />
                </div>
                <div className="flex items-center justify-between w-full gap-2 px-3">
                  <Label className="w-full">{t('stopped_more_than')} {t('in_seconds')}</Label>
                  <input
                    type="number"
                    className="flex h-6 w-16 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 focus:border-primary focus:border-1 focus:ring-primary focus:ring-1 focus:ring-inset"
                    value={
                      filters.find((filter) => filter.id == "stopped")
                        ? (filters.find((filter) => filter.id == "stopped")?.value as number)
                        : 0
                    }
                    onChange={(e) => {
                      changeFilter("stopped", Number(e.target.value), false);
                    }}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
            {filtersColumns.map((category) => (
              <TableFilterCategory
                key={category.columnId}
                content={category.category}
                categoryName={category.categoryName}
                columnId={category.columnId}
                filterState={state}
              />
            ))}
          </Accordion>
        </div>
      </PopoverContent>

    </Popover>
  );
};

export default PersonsFilters;
